import { useEffect, useRef } from "react";
import type { User } from "../../../../interfaces/user";
import { useLiveSessionStore } from "../../../../stores/useLiveSessionStore";
import { useUserContext } from "../../../../stores/userContext";

// Environment configuration
const ENGINES_BASE_PATH = import.meta.env.VITE_ENGINES_BASE_PATH || "";
const PACKAGE_BASE_PATH = import.meta.env.VITE_PACKAGE_BASE_PATH || "";
const API_ENV = import.meta.env.MODE || "development"; // "development", "beta", or "production"

// Firebase configuration from environment variables
const FIREBASE_CONFIG = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "",
};

export const NagwaRenderingEngine = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { user } = useUserContext();

  const setIsEngineLoaded = useLiveSessionStore(
    (state) => state.setIsEngineLoaded
  );
  const setEngineError = useLiveSessionStore((state) => state.setEngineError);
  const addScreen = useLiveSessionStore((state) => state.addScreen);
  const sendMessageToEngine = useLiveSessionStore(
    (state) => state.sendMessageToEngine
  );
  const addStudent = useLiveSessionStore((state) => state.addStudent);
  const removeStudent = useLiveSessionStore((state) => state.removeStudent);
  const addChatMessage = useLiveSessionStore((state) => state.addChatMessage);

  const sessionData = useLiveSessionStore((state) => state.sessionData);
  const isVideoOn = useLiveSessionStore((state) => state.isVideoOn);

  const iframeSrc = `${ENGINES_BASE_PATH}/html_rendering_engine/index.html`;
  const iframeOrigin = iframeSrc ? new URL(iframeSrc).origin : "";

  // Initialize engine when iframe loads
  useEffect(() => {
    if (!iframeRef.current || !user || !sessionData?.firebaseToken) {
      console.log("Waiting for engine requirements...");
      return;
    }

    console.log("Initializing Nagwa Rendering Engine...");

    const initializeEngine = () => {
      if (!iframeRef.current?.contentWindow) {
        console.error("Iframe not accessible");
        setEngineError("Iframe not accessible");
        return;
      }

      const config = {
        role: "educator" as const,
        name: user.firstName || user.email || "Educator",
        rootElementID: "root",
        userID: String(user.userId),
        mode: "live",
        apiEnv: API_ENV,
        tokens: {
          accessToken: {
            token: user.accessToken || null,
            tokenExp: user.accessTokenExp || null,
          },
          refreshToken: {
            token: user.refreshToken || null,
            tokenExp: user.refreshTokenExp || null,
          },
        },
        sessionID: sessionData.sessionId,
        materialID: sessionData.materialId || "",
        material_md5: sessionData.materialMd5 || "",
        firebaseToken: sessionData.firebaseToken,
        target: "web",
        packageBasePath: sessionData.materialId
          ? `${PACKAGE_BASE_PATH}/${sessionData.materialId}/${sessionData.materialId}`
          : PACKAGE_BASE_PATH,
        enginesBasePath: ENGINES_BASE_PATH,
        firebaseConfig: FIREBASE_CONFIG,
      };

      console.log("Engine Config:", config);

      try {
        const initMessage = {
          action: "init",
          target: "NagwaRenderingEngine",
          engineConfig: {
            type: "web",
            enginesDomain: iframeOrigin,
            appDomain: "",
          },
          payload: config,
        };

        console.log("Sending initialization message to engine...");
        iframeRef.current.contentWindow.postMessage(
          JSON.stringify(initMessage),
          iframeOrigin
        );
        console.log("Engine initialization message sent");
      } catch (error) {
        console.error("Failed to initialize engine:", error);
        setEngineError("Failed to initialize engine");
      }
    };

    const iframe = iframeRef.current;
    iframe.onload = initializeEngine;
    return () => {
      iframe.removeEventListener("load", initializeEngine);
    };
  }, [user, sessionData, iframeOrigin]);

  // Listen for messages from engine
  useEffect(() => {
    // State variables for action processing
    let isStreaming: boolean | null = null;
    let streamingEducatorId: string | null = null;
    let audioState: boolean | null = null;
    let raiseHand: boolean | null = null;
    let anotherStudentSpeak: string | null = null;

    // Helper function to parse question ID and part ID
    const getQuestionIdAndPartId = (questionID: string): [string, string] => {
      const parts = questionID.split("_");
      return [parts[0] || "", parts[1] || ""];
    };

    // Handle individual Firebase action
    const handleAction = (action: any) => {
      console.log("Processing action:", action);
      try {
        switch (action.action) {
          case "chat_message_session": {
            const senderName =
              action.payload?.sender?.name ||
              action.payload?.sender_name ||
              "Unknown user";
            const senderType = action.payload?.sender?.type;
            const messageId = action?.id || "";
            const messageText =
              action.payload?.message ||
              action.payload?.text ||
              action.payload?.payload;

            if (messageText &&!action.payload?.isDeleted) {
              
              addChatMessage({
                id: messageId,
                username: senderName,
                message: messageText,
                action: "message",
                userRole: senderType === "educator" ? "tutor" : "student",
              });
            }
            break;
          }
          case "start_stream":
            break;

          case "stop_stream":
            break;

          case "grant_request_mic": // When tutor accepts raise hand
            break;

          case "withdraw_request_mic": // When tutor rejects raise hand
            break;

          case "grant_all_access_mic": // When tutor accepts raise hand for all
            break;

          case "withdraw_all_access_mic": // When tutor rejects raise hand for all
            break;

          case "kick_out_session": {
            const receiverId = action.payload?.receiver?.id;
            const receiverName =
              action.payload?.receiver?.name || "Unknown participant";
            if (receiverId) {
              removeStudent(receiverId); 
              addChatMessage({
                id: receiverId,
                username: "System",
                message: `${receiverName} has been removed from the session.`,
                action: "kick_out_session",
              });
            }
            break;
          }

          case "leave_session": {
            const senderId = action.payload?.sender?.id;
            const senderName =
              action.payload?.sender?.name || "Unknown participant";
            if (senderId) {
              removeStudent(senderId);
        
              addChatMessage({
                id: senderId,
                username: "System",
                message: `${senderName} left the session.`,
                action: "leave_session",
              });
            }
            break;
          }

          case "join_session": {
            const senderId = action.payload?.sender?.id;
            const senderName = action.payload?.sender?.name;

            if (senderId && senderName) {
              addStudent({
                userId: senderId,
                firstName: senderName,
              } as User);
 
              addChatMessage({
                id: senderId,
                username: "System",
                message: `${senderName} joined the session.`,
                action: "join_session",
              });
            }
            break;
          }

          default:
            console.log("Unhandled action:", action.action);
        }
      } catch (error) {
        console.error("Error handling action:", error);
      }
    };

    // Execute accumulated engine actions (update state/callbacks after processing all actions)
    const executeEngineActions = () => {
      // Handle video stream state
      if (isStreaming !== null) {
        console.log("Streaming status:", {
          isStreaming,
          educatorId: streamingEducatorId,
        });
        // Can be handled by parent component if needed
      }

      // Handle audio state
      if (audioState !== null) {
        console.log("Audio state:", audioState);
        // Can be handled by LiveKit component
      }

      // Handle raise hand state
      if (raiseHand !== null) {
        console.log("Raise hand state:", raiseHand);
      }

      // Handle another student speaking
      if (anotherStudentSpeak !== null) {
        console.log("Another student speaking:", anotherStudentSpeak);
      }
    };

    const handleMessage = (event: MessageEvent) => {
      console.log(
        "[NagwaRenderingEngine] Received message from origin:",
        event.origin
      );
      console.log("[NagwaRenderingEngine] Expected origin:", iframeOrigin);
      // debugger;
      setIsEngineLoaded(true);
      // Security: Validate message origin
      if (event.origin !== iframeOrigin) {
        console.warn(
          "[NagwaRenderingEngine] Message from unexpected origin:",
          event.origin,
          "expected:",
          iframeOrigin
        );
        return;
      }

      if (!event.data) {
        console.warn("[NagwaRenderingEngine] Message has no data");
        return;
      }

      try {
        const parsed = JSON.parse(event.data);
        console.log("[NagwaRenderingEngine] Parsed message:", parsed);
        console.log("[NagwaRenderingEngine] Message Key:", parsed.messageKey);

        // =====================================
        // HANDLE ENGINE MESSAGE KEYS
        // =====================================

        // Handle list of actions (Firebase batch)
        if (
          parsed.messageKey === "actionUpdated" &&
          Array.isArray(parsed.actions)
        ) {
          console.log(
            "[NagwaRenderingEngine] Processing actions array:",
            parsed.actions.length,
            parsed.actions
          );
          parsed.actions.forEach(handleAction);
          executeEngineActions();
          return;
        }

        // Handle single action messages
        if (parsed.action) {
          handleAction(parsed);
          executeEngineActions();
        }

        // Handle allScreens response
        if (parsed.messageKey === "screensUpdated" && parsed.screens) {
          console.log(
            "[NagwaRenderingEngine] Received all screens:",
            parsed.screens.length
          );
          parsed.screens.forEach((screen: any) => {
            const screenData = {
              id: screen.id,
              slide_number: screen.slide_number || "001",
              slide_path: screen.slide_path || `${screen.id}`,
              slide_thumbnail_extension:
                screen.slide_thumbnail_extension || "png",
              type: screen.type,
            };
            console.log("Adding screen from allScreens:", screenData);
            addScreen(screenData);
          });
        }
        console.log(
          "[NagwaRenderingEngine] Handling message key:",
          parsed.messageKey
        );
        switch (parsed.messageKey) {
          // 1. ENGINE LOADED (Status 200 equivalent)
          case "engineLoaded":
            // debugger;
            console.log(
              "[NagwaRenderingEngine] ENGINE LOADED - Setting isEngineLoaded to true"
            );
            setIsEngineLoaded(true);
            // Close audio by default
            audioState = false;

            // Request all screens data from the engine
            console.log(
              "[NagwaRenderingEngine] Requesting all screens data..."
            );
            sendMessageToEngine("getAllScreens", {});

            executeEngineActions();
            break;

          // 2. INTERACTION UPDATED (Student answered a question)
          case "interactionUpdated":
            console.log("Student interaction:", parsed);
            // Handle student answer data (can be passed to parent)
            break;

          // 3. ENGAGEMENT STATUS UPDATED (Question state changed)
          case "engagementStatusUpdated":
            console.log("Engagement status:", parsed.engagementStatus);
            // EngagementStatus: INPROGRESS, TIMEISUP, CLOSED, etc.
            break;

          // 4. SESSION ENDED (Tutor ended the session)
          case "sessionEnded":
            console.log("Session ended by tutor");
            // Handle session end
            break;

          // 5. NAVIGATE TO EDU RESOURCE (Redirect to exam/resource)
          case "navigateToEduResource":
            console.log(
              "Navigate to resource:",
              parsed.educational_resource_id
            );
            const subject_language = parsed.subject_language;
            const url = `/${subject_language}/sessions/${sessionData?.sessionId}/exams/${parsed.educational_resource_id}`;
            console.log("Redirecting to:", url);
            // Uncomment to enable redirect: window.location.href = url;
            break;

          // 6. CURRENT SCREEN UPDATED (Slide changed)
          case "currentScreenUpdated":
            console.log("Slide changed:", {
              screenID: parsed.screenID,
              screenType: parsed.screenType,
            });

            // Collect screen data for thumbnails
            if (parsed.screenID && parsed.screenType) {
              const screenData = {
                id: parsed.screenID,
                slide_number: parsed.slideNumber || "001",
                slide_path: parsed.slidePath || `${parsed.screenID}`,
                slide_thumbnail_extension:
                  parsed.slideThumbnailExtension || "png",
                type: parsed.screenType,
              };

              // Check if this screen already exists (avoid duplicates)
              const currentScreens = useLiveSessionStore.getState().screenData;
              const existingScreenIndex = currentScreens.findIndex(
                (screen) => screen.id === parsed.screenID
              );

              if (existingScreenIndex === -1) {
                console.log("Adding new screen data:", screenData);
                console.log("Current screen count:", currentScreens.length);
                addScreen(screenData);
              } else {
                console.log(
                  "Screen already exists at index:",
                  existingScreenIndex,
                  ", skipping:",
                  parsed.screenID
                );
              }
            }

            // Track analytics (if tracking is set up)
            break;

          // 7. SESSION ACTIVITY SLICE RENDERED (Activity loaded)
          case "sessionActivitySliceRendered":
            console.log("Activity loaded:", {
              activityID: parsed.activityID,
              type: parsed.type,
              sliceId: parsed.sliceId,
            });
            // Track analytics
            break;

          // 8. SESSION ACTIVITY SLICE SOLVED (Activity answered)
          case "sessionActivitySliceSolved":
            console.log("Activity solved:", {
              activityID: parsed.activityID,
              isCorrect: parsed.isCorrect,
            });
            // Track analytics
            break;

          // 9. SESSION QUESTION RENDERED (Question displayed)
          case "sessionQuestionRendered":
            console.log("Question rendered:", {
              questionID: parsed.questionID,
              partsETSTypes: parsed.partsETSTypes,
            });
            const [qId, qPartId] = getQuestionIdAndPartId(parsed.questionID);
            console.log("Question ID:", qId, "Part ID:", qPartId);
            // Track analytics
            break;

          // 10. STUDENT ANSWER (Question answered)
          case "studentAnswer":
            console.log("Student answer:", {
              questionID: parsed.questionID,
              isCorrect: parsed.isStudentAnswerCorrect,
              mark: parsed.studentMark,
            });
            const [answerId, answerPartId] = getQuestionIdAndPartId(
              parsed.questionID
            );
            console.log(
              "Answer for Question ID:",
              answerId,
              "Part ID:",
              answerPartId
            );

            // Handle TextAI questions
            if (parsed?.partsETSTypes?.includes("textai")) {
              console.log("TextAI question solved:", {
                templateId: parsed.aiTemplateId,
                studentAnswer: parsed.answer,
                aiResponse: parsed.aiResponse,
              });
            }
            break;

          // 11. ERROR
          case "error":
            console.error("Engine error:", parsed.payload);
            setEngineError(parsed.payload?.message || "Engine error occurred");
            break;

          default:
            console.log("Unhandled message key:", parsed.messageKey);
        }
      } catch (error) {
        console.error("Error parsing engine message:", error);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    iframeOrigin,
    setIsEngineLoaded,
    setEngineError,
    user,
    sessionData,
    addStudent,
    removeStudent,
    addChatMessage,
  ]);



  return (<>
  {iframeSrc && (
    <iframe
      ref={iframeRef}
      src={iframeSrc}
      width="100%"
      height="100%"
      title="Nagwa Rendering Engine"
      allow="camera; microphone; display-capture"
      sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups"
      style={{
        border: "none",
        display: isVideoOn ? "none" : "block"
      }}
    />
  )}
</>
  );
};
